import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchHospitalCompanies,
  fetchHospitalConventions,
  submitHospitalInvoice,
  type HospitalCompanyOption,
  type HospitalConventionOption,
} from "../api/hospitalApi";
import { getAuthToken } from "../auth";
import type { InvoiceCurrencyCode } from "../components/InvoiceCurrencyFields";
import { isValidExchangeRateCdf } from "../components/InvoiceCurrencyFields";

export type HospitalSubmitForm = {
  companyId: string;
  conventionId: string;
  periodFrom: string;
  periodTo: string;
  providerReference: string;
  file: File | null;
  invoiceCurrency: InvoiceCurrencyCode;
  exchangeRateCdf: string;
  totalAmount: string;
  rsspAmount: string;
  notes: string;
  nature: "forfait_mensuel" | "hors_forfait";
  forfaitNbFamilles: string;
  forfaitNbFamillesClient: string;
};

const initialForm: HospitalSubmitForm = {
  companyId: "",
  conventionId: "",
  periodFrom: "",
  periodTo: "",
  providerReference: "",
  file: null,
  invoiceCurrency: "USD",
  exchangeRateCdf: "",
  totalAmount: "",
  rsspAmount: "",
  notes: "",
  nature: "hors_forfait",
  forfaitNbFamilles: "",
  forfaitNbFamillesClient: "",
};

export function useHospitalInvoiceSubmit() {
  const [form, setForm] = useState<HospitalSubmitForm>(initialForm);
  const [hospitalName, setHospitalName] = useState("Hôpital");
  const [companies, setCompanies] = useState<HospitalCompanyOption[]>([]);
  const [conventions, setConventions] = useState<HospitalConventionOption[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingConventions, setLoadingConventions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedInvoiceId, setSubmittedInvoiceId] = useState<number | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const updateForm = useCallback(<K extends keyof HospitalSubmitForm>(key: K, value: HospitalSubmitForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const selectedCompany = useMemo(
    () => companies.find((company) => String(company.id) === form.companyId) ?? null,
    [companies, form.companyId],
  );

  const selectedConvention = useMemo(
    () => conventions.find((convention) => String(convention.id) === form.conventionId) ?? null,
    [conventions, form.conventionId],
  );

  const isForfait = form.nature === "forfait_mensuel";

  const loadCompanies = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      return;
    }

    setLoadingMeta(true);
    setError(null);
    setUsingApi(true);
    try {
      const response = await fetchHospitalCompanies();
      setHospitalName(response.hospital_name);
      setCompanies(response.companies ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les entreprises.");
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (!getAuthToken() || !form.companyId) {
      setConventions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingConventions(true);
      setError(null);
      try {
        const response = await fetchHospitalConventions({
          company_id: Number(form.companyId),
          periode_debut: form.periodFrom || undefined,
          periode_fin: form.periodTo || undefined,
        });
        if (cancelled) return;
        const rows = response.conventions ?? [];
        setConventions(rows);
        setForm((current) => {
          const stillValid = rows.some((row) => String(row.id) === current.conventionId);
          if (stillValid) return current;
          if (rows.length === 1) {
            const only = rows[0];
            const natureDefault =
              only.nature_defaut === "forfait_mensuel" ||
              only.type_traitement === "forfaitaire" ||
              only.type_traitement === "mixte"
                ? "forfait_mensuel"
                : "hors_forfait";
            return {
              ...current,
              conventionId: String(only.id),
              nature: natureDefault,
            };
          }
          return { ...current, conventionId: "" };
        });
      } catch (err) {
        if (!cancelled) {
          setConventions([]);
          setError(err instanceof Error ? err.message : "Impossible de charger les conventions.");
        }
      } finally {
        if (!cancelled) setLoadingConventions(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [form.companyId, form.periodFrom, form.periodTo]);

  useEffect(() => {
    if (!selectedConvention) return;
    const type = selectedConvention.type_traitement ?? "";
    if (type === "forfaitaire" || type === "mixte") {
      setForm((current) => {
        if (current.nature === "forfait_mensuel") return current;
        return { ...current, nature: "forfait_mensuel" };
      });
    } else if (type === "tiers_payant") {
      setForm((current) => {
        if (current.nature === "hors_forfait") return current;
        return { ...current, nature: "hors_forfait" };
      });
    }
  }, [selectedConvention]);

  useEffect(() => {
    if (!selectedConvention?.forfait_preview || form.nature !== "forfait_mensuel") return;
    const nb = String(
      selectedConvention.forfait_preview.nb_familles ??
        selectedConvention.forfait_preview.nb_employes ??
        "",
    );
    if (!nb) return;
    setForm((current) => ({
      ...current,
      forfaitNbFamilles: current.forfaitNbFamilles || nb,
      forfaitNbFamillesClient: current.forfaitNbFamillesClient || nb,
    }));
  }, [selectedConvention, form.nature]);

  const submit = useCallback(async () => {
    if (!getAuthToken()) {
      setError("Connexion API requise pour soumettre une facture.");
      return null;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (form.invoiceCurrency === "CDF" && !isValidExchangeRateCdf(form.exchangeRateCdf)) {
        throw new Error("Taux FC/USD invalide (entre 100 et 50 000).");
      }
      const invoice = await submitHospitalInvoice({
        company_id: Number(form.companyId),
        convention_id: form.conventionId ? Number(form.conventionId) : undefined,
        periode_debut: form.periodFrom,
        periode_fin: form.periodTo,
        nature_facture: form.nature,
        total_amount: form.totalAmount ? Number(form.totalAmount) : undefined,
        montant_rssp: form.rsspAmount ? Number(form.rsspAmount) : 0,
        invoice_currency: form.invoiceCurrency,
        exchange_rate_cdf:
          form.invoiceCurrency === "CDF" ? Number(form.exchangeRateCdf) : undefined,
        reference_prestataire: form.providerReference || undefined,
        submission_notes: form.notes || undefined,
        forfait_nb_familles: form.forfaitNbFamilles
          ? Number(form.forfaitNbFamilles)
          : undefined,
        forfait_nb_familles_client: form.forfaitNbFamillesClient
          ? Number(form.forfaitNbFamillesClient)
          : undefined,
        invoice_file: form.nature === "hors_forfait" ? form.file : form.file,
      });
      setSubmittedInvoiceId(invoice.id);
      return invoice;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Soumission impossible.");
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  const reset = useCallback(() => {
    setForm(initialForm);
    setSubmittedInvoiceId(null);
    setError(null);
  }, []);

  return {
    form,
    updateForm,
    hospitalName,
    companies,
    conventions,
    selectedCompany,
    selectedConvention,
    isForfait,
    loadingMeta,
    loadingConventions,
    submitting,
    error,
    submittedInvoiceId,
    usingApi,
    submit,
    reset,
  };
}
